import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Label } from "./ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// --- Solution catalogue ----------------------------------------------------
// Single source of truth for the dropdown — flat list of demoable PrimeS4
// platforms. Each entry carries an optional `desc` shown under the option
// label for context in the dropdown.
const SOLUTIONS = [
  {
    key: "warehousex-platform",
    label: "WarehouseX Platform Demo",
    desc: "Overall WarehouseX ecosystem walkthrough",
  },
  {
    key: "smartevaluate",
    label: "SmartEvaluate Demo",
    desc: "Warehouse assessment & recommendation platform",
  },
  {
    key: "smartimprove",
    label: "SmartImprove Demo",
    desc: "Operational improvement & optimization platform",
  },
  {
    key: "smartrf",
    label: "SmartRF Demo",
    desc: "SAP warehouse mobility & RF modernization",
  },
  {
    key: "smartinspect",
    label: "SmartInspect Demo",
    desc: "Logistics inspection & operational intelligence",
  },
];

const ALL_MODULES = SOLUTIONS;

const EMPTY_FORM = {
  full_name: "",
  company_name: "",
  business_email: "",
  country_region: "",
  solution: "",
  interest_areas: [],
  notes: "",
};

// --- Path → solution mapping -----------------------------------------------
// `/smartintegrations/smartinspect` → "smartinspect" (key match against the
// last path segment). Falls back to single-module pillar match
// (e.g. `/smartstrategy` → SmartEvaluate).
function resolveSolutionFromPath(pathname) {
  if (!pathname) return "";
  const parts = pathname.split("/").filter(Boolean);
  for (let i = parts.length - 1; i >= 0; i--) {
    const seg = parts[i].toLowerCase();
    const direct = ALL_MODULES.find((m) => m.key === seg);
    if (direct) return direct.key;
  }
  return "";
}

/**
 * RequestDemoModal — global, event-driven.
 * Mount once at the app root. Any caller can open it via:
 *     window.dispatchEvent(new CustomEvent("requestDemo:open", { detail: { solution: "smartrf" } }))
 * Pre-selection auto-detects from the current pathname when no detail is sent.
 */
export default function RequestDemoModal() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    function onOpen(e) {
      const detail = e.detail || {};
      const presel =
        detail.solution
        || resolveSolutionFromPath(typeof window !== "undefined" ? window.location.pathname : "");
      setForm({ ...EMPTY_FORM, solution: presel || "" });
      setOpen(true);
    }
    window.addEventListener("requestDemo:open", onOpen);
    return () => window.removeEventListener("requestDemo:open", onOpen);
  }, []);

  function update(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.full_name || !form.company_name || !form.business_email
        || !form.country_region || !form.solution) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const solutionLabel = ALL_MODULES.find((m) => m.key === form.solution)?.label || form.solution;
      await axios.post(`${API}/demo-requests`, {
        ...form,
        solution: solutionLabel,
        source_path: typeof window !== "undefined" ? window.location.pathname : "",
      });
      toast.success("Demo request received — our team will be in touch shortly.");
      setOpen(false);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit your request. Try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        data-testid="request-demo-modal"
        className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-sm border border-[#E5E7EB] shadow-xl"
      >
        <DialogHeader>
          <DialogTitle
            className="text-2xl font-bold text-[#111827] tracking-tight"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Request a Demo
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280] leading-relaxed">
            Schedule a walkthrough of PrimeS4 intelligent warehouse transformation platforms and SAP-driven operational solutions.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-6 mt-2">
          {/* Section 1 — Contact */}
          <section className="space-y-4">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[#025979]">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full Name" required>
                <Input
                  data-testid="demo-field-full-name"
                  value={form.full_name}
                  onChange={(e) => update("full_name", e.target.value)}
                  className="rounded-sm"
                  required
                />
              </Field>
              <Field label="Company Name" required>
                <Input
                  data-testid="demo-field-company-name"
                  value={form.company_name}
                  onChange={(e) => update("company_name", e.target.value)}
                  className="rounded-sm"
                  required
                />
              </Field>
              <Field label="Business Email" required>
                <Input
                  data-testid="demo-field-email"
                  type="email"
                  value={form.business_email}
                  onChange={(e) => update("business_email", e.target.value)}
                  className="rounded-sm"
                  required
                />
              </Field>
              <Field label="Country / Region" required>
                <Input
                  data-testid="demo-field-country"
                  value={form.country_region}
                  onChange={(e) => update("country_region", e.target.value)}
                  className="rounded-sm"
                  required
                />
              </Field>
            </div>
          </section>

          {/* Section 2 — Solution */}
          <section className="space-y-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[#025979]">
              Solution Interest
            </h3>
            <Field label="Select Solution" required>
              <Select
                value={form.solution}
                onValueChange={(v) => update("solution", v)}
              >
                <SelectTrigger data-testid="demo-field-solution" className="rounded-sm">
                  <SelectValue placeholder="Choose a PrimeS4 solution" />
                </SelectTrigger>
                <SelectContent className="p-1">
                  {SOLUTIONS.map((m) => (
                    <SelectItem
                      key={m.key}
                      value={m.key}
                      className={
                        // Clean enterprise hover — light neutral background
                        // with a subtle left accent. Keeps the supporting
                        // subtitle fully readable (no heavy teal wash).
                        "group/item relative py-2 pl-3 pr-8 rounded-sm cursor-pointer transition-colors "
                        + "border-l-2 border-transparent "
                        + "focus:bg-[#F1F5F9] focus:text-[#0F172A] focus:border-l-[#94A3B8] "
                        + "data-[highlighted]:bg-[#F1F5F9] data-[highlighted]:text-[#0F172A] data-[highlighted]:border-l-[#94A3B8] "
                        + "data-[state=checked]:bg-[#E0F2FE] data-[state=checked]:text-[#025979] data-[state=checked]:border-l-[#0093CB] "
                        + "focus:outline-none"
                      }
                    >
                      <div className="flex flex-col gap-0.5 leading-tight">
                        <span className="text-[13px] font-semibold text-[#111827] group-hover/item:text-[#0F172A] group-focus/item:text-[#0F172A] group-data-[state=checked]/item:text-[#025979]">
                          {m.label}
                        </span>
                        {m.desc && (
                          <span className="text-[11px] text-[#64748B]">
                            {m.desc}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </section>

          {/* Section 3 — Notes */}
          <section className="space-y-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.10em] text-[#025979]">
              Additional Notes
            </h3>
            <Textarea
              data-testid="demo-field-notes"
              placeholder="Tell us briefly about your business challenges, transformation goals, SAP landscape, or solution interest."
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              className="rounded-sm min-h-[88px]"
            />
          </section>

          {/* CTA */}
          <div className="pt-2 border-t border-[#E5E7EB]">
            <Button
              type="submit"
              data-testid="demo-submit-btn"
              disabled={submitting}
              className="w-full rounded-sm h-10 bg-[#025979] hover:bg-[#03738C] text-white font-semibold disabled:opacity-60"
            >
              {submitting ? "Sending request…" : "Request Demo"}
            </Button>
            <p className="mt-2 text-[11px] text-center text-[#6B7280]">
              Our PrimeS4 team will contact you to schedule a personalized walkthrough.
            </p>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Tiny labelled-field helper kept local to this file so we don't grow the
// shared component library for a one-screen form.
function Field({ label, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-[#374151]">
        {label}{required && <span className="text-[#DC2626] ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}
